import React, { useState } from "react";
import {
  Button,
  Modal,
  Form,
  Table,
  InputGroup,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { db, storage } from "../../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../../../context/AuthContext";
import {
  collection,
  doc,
  updateDoc,
  addDoc,
  Timestamp,
  query,
  where,
  getDocs,
  getDoc,
  deleteDoc,
  setDoc,
} from "firebase/firestore";
import "../Inventory/BatchCSS.css";
import swal from "sweetalert";
import {
  MaskActivityOptions,
  MaskSizes,
} from "./formdata/MaskInventoryData";

const MaskInventoryTable = ({ inventoryList, fetchInventory, maskLogs, maskOptions = [], centerFilter, nodeOptions = [] }) => {
  const { currentUser } = useAuth();

  // Borrow period options (shorter than rental)
  const BorrowPeriods = ["1 week", "2 weeks", "1 month", "3 months"];

  // Generate sequential order ID (T{YY}{SSS} format)
  const getNextMaskTransactionSequence = async () => {
    const currentDate = new Date();
    const thaiYear = currentDate.getFullYear() + 543;
    const yearStr = thaiYear.toString().slice(2);

    const sequenceRef = doc(db, "sequenceTracker", "maskTransactionSequence");
    const sequenceDoc = await getDoc(sequenceRef);

    if (sequenceDoc.exists()) {
      const data = sequenceDoc.data();
      if (data.year === yearStr) {
        const newSequence = (parseInt(data.current, 10) + 1).toString().padStart(3, "0");
        await updateDoc(sequenceRef, { current: newSequence });
        return { sequence: newSequence, year: yearStr };
      }
    }
    // New year or first time - reset to 001
    await setDoc(sequenceRef, { current: "001", year: yearStr });
    return { sequence: "001", year: yearStr };
  };

  const generateOrderId = async () => {
    const { sequence, year } = await getNextMaskTransactionSequence();
    return `T${year}${sequence}`;
  };

  // Edit modal selected item
  const [selectedItem, setSelectedItem] = useState(null);

  // Batch update modal states
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchUpdates, setBatchUpdates] = useState([
    {
      id: 1,
      selectedItem: null,
      activityType: "Borrow",
      transaction: {
        amount: "",
        datetime: new Date().toISOString().slice(0, 10),
        dueDate: "",
        rentalPeriod: "",
        borrowPeriod: "1 week",
        price: "",
        note: "",
      },
    },
  ]);

  const [batchGlobal, setBatchGlobal] = useState({
    HN: "",
    firstname: "",
    lastname: "",
    mobile: "",
  });
  const [isBatchSubmitting, setIsBatchSubmitting] = useState(false);
  const [nameSearch, setNameSearch] = useState("");

  // Edit mask info modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    itemId: "",
    maskName: "",
    size: "",
    price: "",
    available: "",
    center: "",
  });
  const [editCustomMaskName, setEditCustomMaskName] = useState(false);
  const [editSelectedImage, setEditSelectedImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);

  // Image hover preview state
  const [hoverImage, setHoverImage] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  // Open edit mask info modal
  const openEditModal = (item) => {
    if (!item) {
      console.error("openEditModal received an undefined item");
      return;
    }
    setSelectedItem(item);
    setEditForm({
      itemId: item.itemId || "",
      maskName: item.maskName || "",
      size: item.size || "",
      price: item.price || "",
      available: item.available || 0,
      center: item.center || "",
    });
    setEditCustomMaskName(!maskOptions.some((m) => m.maskName === item.maskName));
    setEditImagePreview(item.imageUrl || null);
    setEditSelectedImage(null);
    setShowEditModal(true);
  };

  // Handle edit form input change
  const handleEditInputChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  // Handle edit image change
  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle edit form submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (
      !editForm.itemId ||
      !editForm.maskName ||
      !editForm.size ||
      !editForm.price
    ) {
      swal("Please fill all required fields.", "", "warning");
      return;
    }

    try {
      const itemRef = doc(db, "maskInventory", selectedItem.id);
      const updateData = {
        itemId: editForm.itemId,
        maskName: editForm.maskName,
        size: editForm.size,
        price: parseFloat(editForm.price),
        available: parseInt(editForm.available, 10),
        center: editForm.center,
      };

      // Upload new image if selected
      if (editSelectedImage) {
        const storageRef = ref(
          storage,
          `maskInventory/${selectedItem.id}/${editSelectedImage.name}`,
        );
        await uploadBytes(storageRef, editSelectedImage);
        const downloadURL = await getDownloadURL(storageRef);
        updateData.imageUrl = downloadURL;
      }

      await updateDoc(itemRef, updateData);

      swal("Success!", "Mask info updated successfully.", "success");
      setShowEditModal(false);
      fetchInventory();
    } catch (error) {
      console.error("Error updating mask info:", error);
      swal("Error!", "An error occurred while updating mask info.", "error");
    }
  };

  // Calculate due date from period
  const calculateDueDate = (startDate, period) => {
    const date = new Date(startDate);
    switch (period) {
      case "1 week":
        date.setDate(date.getDate() + 7);
        break;
      case "2 weeks":
        date.setDate(date.getDate() + 14);
        break;
      case "1 month":
        date.setMonth(date.getMonth() + 1);
        break;
      case "3 months":
        date.setMonth(date.getMonth() + 3);
        break;
      default:
        break;
    }
    return date;
  };

  // Batch modal handlers
  const openBatchModal = () => {
    setBatchUpdates([
      {
        id: 1,
        selectedItem: null,
        activityType: "Borrow",
        transaction: {
          amount: "",
          datetime: new Date().toISOString().slice(0, 10),
          dueDate: "",
          rentalPeriod: "",
          borrowPeriod: "1 week",
          price: "",
          note: "",
        },
      },
    ]);
    setBatchGlobal({
      HN: "",
      firstname: "",
      lastname: "",
      mobile: "",
    });
    setNameSearch("");
    setShowBatchModal(true);
  };

  const addBatchRow = () => {
    const newId =
      batchUpdates.length > 0
        ? Math.max(...batchUpdates.map((row) => row.id)) + 1
        : 1;
    setBatchUpdates((prev) => [
      ...prev,
      {
        id: newId,
        selectedItem: null,
        activityType: "Borrow",
        transaction: {
          amount: "",
          datetime: new Date().toISOString().slice(0, 10),
          dueDate: "",
          rentalPeriod: "",
          borrowPeriod: "1 week",
          price: "",
          note: "",
        },
      },
    ]);
  };

  const handleBatchChange = (rowId, field, value) => {
    setBatchUpdates((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId
          ? {
              ...row,
              transaction: { ...row.transaction, [field]: value },
            }
          : row,
      ),
    );
  };

  const handleBatchActivityChange = (rowId, value) => {
    setBatchUpdates((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId
          ? {
              ...row,
              activityType: value,
              transaction: { ...row.transaction, borrowPeriod: "" },
            }
          : row,
      ),
    );
  };

  const handleBatchPeriodChange = (rowId, value) => {
    setBatchUpdates((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId
          ? {
              ...row,
              transaction: { ...row.transaction, borrowPeriod: value },
            }
          : row,
      ),
    );
  };

  const handleBatchItemSelect = (rowId, itemId) => {
    const item = inventoryList.find((inv) => inv.itemId === itemId);
    setBatchUpdates((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId
          ? {
              ...row,
              selectedItem: item,
              transaction: {
                ...row.transaction,
                price: item && item.price ? item.price : "",
              },
            }
          : row,
      ),
    );
  };

  const handleGlobalChange = (field, value) => {
    setBatchGlobal((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onSearchPatient = async () => {
    if (!batchGlobal.HN) return;
    try {
      const q = query(
        collection(db, "patients"),
        where("hn", "==", batchGlobal.HN),
      );
      const querySnapshot = await getDocs(q);
      let listPatient = [];
      querySnapshot.forEach((doc) => {
        listPatient.push(doc.data());
      });
      if (listPatient.length > 0) {
        setBatchGlobal((prev) => ({
          ...prev,
          firstname: listPatient[0].firstnameTh || "",
          lastname: listPatient[0].lastnameTh || "",
          mobile: listPatient[0].tel || listPatient[0].mobile || "",
        }));
      } else {
        swal("Patient not found!", "", "warning");
      }
    } catch (error) {
      console.error("Error searching patient:", error);
    }
  };

  const onSearchPatientByName = async () => {
    if (!nameSearch) return;
    try {
      const q = query(
        collection(db, "patients"),
        where("firstnameTh", ">=", nameSearch),
        where("firstnameTh", "<=", nameSearch + "\uf8ff"),
      );
      const querySnapshot = await getDocs(q);
      let listPatient = [];
      querySnapshot.forEach((doc) => {
        listPatient.push(doc.data());
      });
      if (listPatient.length > 0) {
        setBatchGlobal((prev) => ({
          ...prev,
          HN: listPatient[0].hn || "",
          firstname: listPatient[0].firstnameTh || "",
          lastname: listPatient[0].lastnameTh || "",
          mobile: listPatient[0].tel || listPatient[0].mobile || "",
        }));
        setNameSearch("");
      } else {
        swal("Patient not found!", "", "warning");
      }
    } catch (error) {
      console.error("Error searching patient by name:", error);
    }
  };

  const handleBatchSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (isBatchSubmitting) return;
    setIsBatchSubmitting(true);

    try {
      // Generate sequential order ID (T{YY}{SSS} format)
      const orderId = await generateOrderId();

      // DEBUG: Log batch rows
      console.log("=== BATCH SUBMIT DEBUG ===");
      console.log("Total rows:", batchUpdates.length);
      console.log(
        "Rows:",
        batchUpdates.map((r) => ({
          id: r.id,
          itemId: r.selectedItem ? r.selectedItem.id : null,
          itemName: r.selectedItem ? r.selectedItem.maskName : null,
          amount: r.transaction.amount,
        })),
      );

      for (const row of batchUpdates) {
        console.log(
          "Processing row:",
          row.id,
          row.selectedItem ? row.selectedItem.maskName : null,
        );
        if (
          !row.selectedItem ||
          !row.transaction.amount ||
          parseInt(row.transaction.amount, 10) <= 0
        ) {
          swal(
            "Please fill all fields correctly for each batch row.",
            "",
            "warning",
          );
          setIsBatchSubmitting(false);
          return;
        }

        const amount = parseInt(row.transaction.amount, 10);

        // Fetch fresh inventory data to avoid stale values
        const itemRef = doc(db, "maskInventory", row.selectedItem.id);
        const itemSnap = await getDoc(itemRef);
        const currentData = itemSnap.data();

        if (
          (row.activityType === "Borrow" || row.activityType === "Rent") &&
          amount > parseInt(currentData.available, 10)
        ) {
          swal(
            `Insufficient stock for item ${row.selectedItem.maskName}.`,
            "",
            "error",
          );
          setIsBatchSubmitting(false);
          return;
        }

        if (row.activityType === "Borrow" && !row.transaction.borrowPeriod) {
          swal(
            "Please select a borrow period for all Borrow items.",
            "",
            "warning",
          );
          setIsBatchSubmitting(false);
          return;
        }

        let newAvailable = parseInt(currentData.available, 10);
        let newBorrowed = parseInt(currentData.borrowed || 0, 10);
        let newRented = parseInt(currentData.rented || 0, 10);

        if (row.activityType === "Borrow") {
          newAvailable -= amount;
          newBorrowed += amount;
        } else if (row.activityType === "Rent") {
          newAvailable -= amount;
          newRented += amount;
        } else if (row.activityType === "Return") {
          newAvailable += amount;
          if (newBorrowed >= amount) {
            newBorrowed -= amount;
          } else {
            const remaining = amount - newBorrowed;
            newBorrowed = 0;
            newRented -= remaining;
          }
        }

        console.log(
          "Before update:",
          row.selectedItem ? row.selectedItem.maskName : null,
          {
            currentAvailable: currentData.available,
            currentBorrowed: currentData.borrowed,
            newAvailable,
            newBorrowed,
            newRented,
            amount,
          },
        );

        await updateDoc(itemRef, {
          available: newAvailable,
          borrowed: newBorrowed,
          rented: newRented,
        });

        console.log(
          "Updated:",
          row.selectedItem ? row.selectedItem.maskName : null,
        );

        const historyRef = collection(itemRef, "maskLogs");
        await addDoc(historyRef, {
          orderId: orderId,
          activity: row.activityType,
          amount: amount,
          status: row.activityType === "Return" ? "+" : "-",
          datetime: Timestamp.fromDate(new Date(row.transaction.datetime)),
          dueDate:
            row.activityType === "Borrow"
              ? Timestamp.fromDate(
                  calculateDueDate(
                    row.transaction.datetime,
                    row.transaction.borrowPeriod,
                  ),
                )
              : null,
          borrowPeriod:
            row.activityType === "Borrow" ? row.transaction.borrowPeriod : "",
          rentalPeriod: row.transaction.rentalPeriod || "",
          price:
            row.activityType === "Rent"
              ? parseFloat(row.transaction.price) || 0
              : 0,
          note: row.transaction.note,
          createdBy: currentUser.displayName,
          isReturned: row.activityType === "Return",
          maskName: row.selectedItem.maskName,
          size: row.selectedItem.size,
          inventoryDocId: row.selectedItem.id,
          center: centerFilter,
          ...batchGlobal,
        });
      }
      swal("Create successful!", "", "success");
      setShowBatchModal(false);
      fetchInventory();
    } catch (error) {
      console.error("Error with batch update:", error);
      swal("Error!", "An error occurred during batch update.", "error");
    } finally {
      setIsBatchSubmitting(false);
    }
  };

  const openDelete = (item) => {
    swal({
      title: "Are you sure?",
      text: "Do you want to delete this item and all of its history?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        swal({
          title: "Really delete?",
          text: "This action cannot be undone. Are you absolutely sure?",
          icon: "warning",
          buttons: true,
          dangerMode: true,
        }).then(async (reallyDelete) => {
          if (reallyDelete) {
            try {
              const itemRef = doc(db, "maskInventory", item.id);
              const historyRef = collection(itemRef, "maskLogs");
              const historySnapshot = await getDocs(historyRef);
              const deletePromises = [];

              historySnapshot.forEach((docSnap) => {
                const historyDocRef = doc(
                  db,
                  "maskInventory",
                  item.id,
                  "maskLogs",
                  docSnap.id,
                );
                deletePromises.push(deleteDoc(historyDocRef));
              });

              await Promise.all(deletePromises);
              await deleteDoc(itemRef);

              swal(
                "Deleted!",
                "The item and its history have been deleted.",
                "success",
              );
              fetchInventory();
            } catch (error) {
              console.error("Error deleting item:", error);
              swal(
                "Error!",
                "An error occurred while deleting the item.",
                "error",
              );
            }
          } else {
            swal("Deletion cancelled.");
          }
        });
      } else {
        swal("Deletion cancelled.");
      }
    });
  };

  const getActivityBadge = (activity) => {
    switch (activity) {
      case "Borrow":
        return <Badge bg="primary">Borrow</Badge>;
      case "Rent":
        return <Badge bg="success">Rent</Badge>;
      case "Return":
        return <Badge bg="info">Return</Badge>;
      default:
        return <Badge bg="secondary">{activity}</Badge>;
    }
  };

  return (
    <div className="row">
      {/* Full-width Inventory Table */}
      <div className="col-lg-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="card-title">Mask Stocks</h4>
            <Button variant="secondary" onClick={openBatchModal}>
              New Order
            </Button>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table mb-0 table-striped">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>#</th>
                    <th style={{ textAlign: "center" }}>ID</th>
                    <th style={{ textAlign: "center" }}>Image</th>
                    <th style={{ textAlign: "center" }}>Mask Name</th>
                    <th style={{ textAlign: "center" }}>Size</th>
                    <th style={{ textAlign: "center" }}>Price</th>
                    <th style={{ textAlign: "center" }}>Available</th>
                    <th style={{ textAlign: "center" }}>Borrowed</th>
                    <th style={{ textAlign: "center" }}>Rented</th>
                    <th style={{ textAlign: "center" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {[...inventoryList]
                    .sort((a, b) => a.itemId.localeCompare(b.itemId))
                    .map((item, index) => (
                      <tr key={index}>
                        <td style={{ textAlign: "center" }}>{index + 1}</td>
                        <td style={{ textAlign: "center" }}>{item.itemId}</td>
                        <td style={{ textAlign: "center" }}>
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt={item.maskName}
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "cover",
                                borderRadius: "4px",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                const rect = e.target.getBoundingClientRect();
                                setHoverPosition({ x: rect.right + 10, y: rect.top });
                                setHoverImage(item.imageUrl);
                              }}
                              onMouseLeave={() => setHoverImage(null)}
                            />
                          ) : (
                            "-"
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>{item.maskName}</td>
                        <td style={{ textAlign: "center" }}>
                          {item.size || "-"}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {item.price ? `${item.price.toLocaleString()}` : "-"}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <Badge bg="success">{item.available || 0}</Badge>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <Badge bg="warning">{item.borrowed || 0}</Badge>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <Badge bg="info">{item.rented || 0}</Badge>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <Link
                            className="btn btn-primary shadow btn-xs sharp mr-2"
                            to="#"
                            onClick={() => openEditModal(item)}
                            title="Edit mask info"
                          >
                            <i className="fa fa-pencil"></i>
                          </Link>
                          <Link
                            className="btn btn-danger shadow btn-xs sharp"
                            to="#"
                            onClick={() => openDelete(item)}
                            title="Delete item"
                          >
                            <i className="flaticon-381-error"></i>
                          </Link>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Mask Info Modal */}
      {selectedItem && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Mask Info</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleEditSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Center <span className="text-danger">*</span>
                </Form.Label>
                <select
                  name="center"
                  className="form-control"
                  value={editForm.center}
                  onChange={handleEditInputChange}
                  required
                >
                  <option value="">Select center</option>
                  {nodeOptions.map((node) => (
                    <option key={node} value={node}>
                      {node}
                    </option>
                  ))}
                </select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Item ID <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="itemId"
                  value={editForm.itemId}
                  onChange={handleEditInputChange}
                  placeholder="e.g., M001"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Mask Name <span className="text-danger">*</span>
                </Form.Label>
                <select
                  name="maskName"
                  className="form-control"
                  value={editCustomMaskName ? "custom" : editForm.maskName}
                  onChange={(e) => {
                    if (e.target.value === "custom") {
                      setEditCustomMaskName(true);
                      setEditForm({ ...editForm, maskName: "", price: "" });
                    } else {
                      setEditCustomMaskName(false);
                      const selectedMask = maskOptions.find(
                        (m) => m.maskName === e.target.value
                      );
                      setEditForm({
                        ...editForm,
                        maskName: e.target.value,
                        price: selectedMask && selectedMask.price ? selectedMask.price : "",
                      });
                    }
                  }}
                >
                  <option value="">Select a mask</option>
                  {maskOptions.map((mask) => (
                    <option key={mask.maskName} value={mask.maskName}>
                      {mask.maskName}
                    </option>
                  ))}
                  <option value="custom">Other (Specify)</option>
                </select>

                {editCustomMaskName && (
                  <Form.Control
                    type="text"
                    name="maskName"
                    placeholder="Enter custom mask name"
                    value={editForm.maskName}
                    onChange={handleEditInputChange}
                    className="mt-2"
                    required
                  />
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Size <span className="text-danger">*</span>
                </Form.Label>
                <select
                  name="size"
                  className="form-control"
                  value={editForm.size}
                  onChange={handleEditInputChange}
                  required
                >
                  <option value="">Select size</option>
                  {MaskSizes.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Price (THB) <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="price"
                  value={editForm.price}
                  onChange={handleEditInputChange}
                  placeholder="e.g., 2500"
                  min="0"
                  step="0.01"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleEditImageChange}
                />
                {editImagePreview && (
                  <div className="mt-2">
                    <img
                      src={editImagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Available Quantity <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="available"
                  value={editForm.available}
                  onChange={handleEditInputChange}
                  placeholder="e.g., 10"
                  min="0"
                  required
                />
              </Form.Group>

              <Button variant="primary" type="submit" className="mt-3">
                Save Changes
              </Button>
            </Form>
          </Modal.Body>
        </Modal>
      )}

      {/* New Order Modal */}
      <Modal
        show={showBatchModal}
        onHide={() => setShowBatchModal(false)}
        dialogClassName="custom-modal-dialog"
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>New Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleBatchSubmit}>
            {/* Global Patient Info */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>HN</Form.Label>
                  {/* <InputGroup>*/}
                  <div className="d-flex align-items-center">
                    <Form.Control
                      className="mr-3"
                      type="text"
                      value={batchGlobal.HN}
                      onChange={(e) => handleGlobalChange("HN", e.target.value)}
                      placeholder="Enter HN"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={onSearchPatient}
                    >
                      Search
                    </Button>
                  </div>
                  {/* </InputGroup>*/}
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>First Name</Form.Label>
                  {/* <InputGroup>*/}
                  <div className="d-flex align-items-center">
                    <Form.Control
                      className="mr-3"
                      type="text"
                      value={nameSearch}
                      onChange={(e) => setNameSearch(e.target.value)}
                      placeholder="Enter firstname"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={onSearchPatientByName}
                    >
                      Search
                    </Button>
                  </div>
                  {/* </InputGroup>*/}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={batchGlobal.firstname}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={batchGlobal.lastname}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile</Form.Label>
                  <Form.Control
                    type="text"
                    value={batchGlobal.mobile}
                    onChange={(e) =>
                      handleGlobalChange("mobile", e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item</th>
                  <th>Available</th>
                  <th>Activity</th>
                  <th style={{ maxWidth: "80px" }}>Qty</th>
                  <th>Date</th>
                  <th>Period</th>
                  <th>Due Date</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {batchUpdates.map((row, index) => (
                  <tr key={row.id}>
                    <td>{index + 1}</td>
                    <td>
                      <Form.Control
                        as="select"
                        value={row.selectedItem ? row.selectedItem.itemId : ""}
                        onChange={(e) =>
                          handleBatchItemSelect(row.id, e.target.value)
                        }
                        required
                      >
                        <option value="">Select Item</option>
                        {[...inventoryList]
                          .sort((a, b) => a.itemId.localeCompare(b.itemId))
                          .map((item) => (
                            <option key={item.id} value={item.itemId}>
                              {item.maskName} ({item.size || "-"})
                            </option>
                          ))}
                      </Form.Control>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {row.selectedItem ? row.selectedItem.available : "N/A"}
                    </td>
                    <td>
                      <Form.Control
                        as="select"
                        value={row.activityType}
                        onChange={(e) =>
                          handleBatchActivityChange(row.id, e.target.value)
                        }
                      >
                        {MaskActivityOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </Form.Control>
                    </td>
                    <td style={{ maxWidth: "80px" }}>
                      <Form.Control
                        type="number"
                        value={row.transaction.amount}
                        onChange={(e) =>
                          handleBatchChange(row.id, "amount", e.target.value)
                        }
                        min="1"
                        required
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="date"
                        value={row.transaction.datetime}
                        onChange={(e) =>
                          handleBatchChange(row.id, "datetime", e.target.value)
                        }
                        required
                      />
                    </td>
                    <td>
                      {row.activityType === "Borrow" ? (
                        <Form.Control
                          as="select"
                          value={row.transaction.borrowPeriod}
                          onChange={(e) =>
                            handleBatchPeriodChange(row.id, e.target.value)
                          }
                          required
                        >
                          <option value="">Select</option>
                          {BorrowPeriods.map((period) => (
                            <option key={period} value={period}>
                              {period}
                            </option>
                          ))}
                        </Form.Control>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {row.activityType === "Borrow" &&
                      row.transaction.borrowPeriod &&
                      row.transaction.datetime
                        ? calculateDueDate(
                            row.transaction.datetime,
                            row.transaction.borrowPeriod,
                          ).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        rows={1}
                        value={row.transaction.note}
                        onChange={(e) =>
                          handleBatchChange(row.id, "note", e.target.value)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <div className="d-flex justify-content-between mt-3">
              <Button variant="secondary" onClick={addBatchRow}>
                +
              </Button>
              <Button
                type="submit"
                variant="success"
                disabled={isBatchSubmitting}
              >
                {isBatchSubmitting ? "Submitting..." : "Submit Order"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Image hover preview overlay */}
      {hoverImage && (
        <div
          style={{
            position: "fixed",
            left: hoverPosition.x,
            top: hoverPosition.y,
            zIndex: 9999,
            pointerEvents: "none",
          }}
        >
          <img
            src={hoverImage}
            alt="Preview"
            style={{
              width: "200px",
              height: "200px",
              objectFit: "cover",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
              border: "2px solid white",
              animation: "zoomIn 0.2s ease-out",
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MaskInventoryTable;
